import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { EMPTY, from, Observable, of, zip } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap, throttleTime, filter, combineLatestWith, take, distinctUntilChanged, mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { MessageService } from 'primeng/api';
import { LocationsService } from '@services/locations/locations.service'
import { BusinessActions, LocationsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { BusinessModel, LocationModel, RequestModel, UpgradeAction, UserModel } from '@models/index';
import { RouterFacadeService } from '@services/router-facade.service';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { emptyLocationModel } from '@utilities/data-utils';
import { HttpApiService } from '@services/http-api.service';

export const throttleIf = <T>(conditional: Observable<boolean>) => {
  return (base: Observable<T>) => {
    return base.pipe(
      combineLatestWith(conditional),
      filter(([_, conditional]) => !conditional),
      map(([current, _]) => current),
    );
  };
}

@Injectable()
export class LocationEffects {

  searchLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.searchLocations),
      map(action => action.pages),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (data: { page: number, size: number }, business: BusinessModel) => ({
          ...data,
          id: business.id
        })
      ),
      switchMap((pages) =>
        this.locationsService
          .searchLocations(pages)
          .pipe(
            map(data => LocationsActions.searchLocationsSuccess({ ...data, page: pages.page })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  searchArchivedLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.searchArchivedLocations),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (data: any, business: BusinessModel) => ({
          businessId: business.id
        })
      ),
      switchMap((businessId) =>
        this.locationsService
          .searchArchivedLocations(businessId)
          .pipe(
            map(data => LocationsActions.searchArchivedLocationsSuccess({ ids: data })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  searchAndNavigateToBusinessLocation$ = createEffect(() => (
    this.actions$.pipe(
      ofType(LocationsActions.searchAndNavigateToBusinessLocation),
      withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
      switchMap(([{ businessId, locationId }, { id }]) => {
        // NOTE: cross reseller navigation is not handled (reseller not selected, undefined behavior)
        if (businessId != id) {
          this.store.dispatch(BusinessActions.searchBusinessById({ businessId }));
        }
        this.store.dispatch(LocationsActions.findLocationById({ locationId }));

        return zip([
          businessId == id
            ? of(null)
            : this.actions$.pipe(ofType(BusinessActions.searchBusinessByIdSuccess)),
          this.actions$.pipe(ofType(LocationsActions.findLocationByIdSuccess))
        ]).pipe(
          withLatestFrom(this.store.select(fromApp.getCurrentRoute)),
          take(1),
        );
      }),
      tap(([_, currentRoute]) => {
        // run after action dispatches
        setTimeout(() => {
          if (currentRoute.includes('admin/')
            || currentRoute.includes('reseller/')
            || currentRoute.includes('admin-business/locations')
          ) {
            this.routerFacadeService.navigateTo$('/admin-business/dashboard');
            //window.location.href = '/admin-business/dashboard';
          } else {
            this.window.location.reload();
          }
        }, 1);
      }),
      map(_ => LocationsActions.searchAndNavigateToBusinessLocationComplete())
    )
  ));

  findLocationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.findLocationById),
      map(action => action.locationId),
      switchMap((locationId) =>
        this.locationsService
          .findLocationById(locationId)
          .pipe(
            filter(data => data ? true : false),
            map(data => LocationsActions.findLocationByIdSuccess({ data })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  searchLocationsByQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.searchLocationByQuery),
      map((action) => ({ page: action.page, size: action.size, query: action.query })),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (payload: { page: number, size: number, query: string }, business: BusinessModel) => ({
          ...payload,
          businessId: business.id
        })
      ),
      switchMap(({ page, size, query, businessId }) =>
        this.locationsService.searchLocationsByQuery({
          page,
          query,
          size,
          businessId
        }).pipe(
          map(data => LocationsActions.searchLocationsSuccess({ ...data, page })),
          catchError(error => from([
            LocationsActions.searchLocationsFailed(),
            LocationsActions.locationError({ error })
          ]))
        )
      )
    )
  )

  addLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.addLocation),
      map(action => action.location),
      throttleTime(4000),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (location: LocationModel, business: BusinessModel) => ({
          ...location,
          businessId: business.id
        })
      ),
      switchMap((location) =>
        this.locationsService
          .addLocation(location)
          .pipe(
            tap(location => this.launchToastMssg('success', 'Create Location', `The location ${location.name} has been created`)),
            mergeMap(location => [
              LocationsActions.addLocationSuccess({ location }),
              LocationsActions.searchLocationByQueryAndLandingUrls({ page: 0, size: 9, query: "" })
            ]),
            tap(() => {
              // temporary workaround to avoid breaking available locations; forces all locations, search bar, etc to reload
              setTimeout(() => {
                this.window.location = 'admin-business/locations';
              }, 2500);
            }),
            catchError(error => {
              this.launchToastMssg('error', 'Create Location', `${error.error.message}`)
              return of(LocationsActions.locationError({ error: error.message }))
            })
          )
      )
    )
  );

  deleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.deleteLocation),
      map(action => action.locationId),
      switchMap((locationId: string) =>
        this.locationsService
          .deleteLocation(locationId)
          .pipe(
            tap(location => this.launchToastMssg('success', 'Delete Location', 'The location has been deleted')),
            map(locationIdDeleted => LocationsActions.deleteLocationSuccess({ locationIdDeleted: locationId })),
            tap(() => {
              this.routerFacadeService.navigateTo$('admin-business/locations');
              this.locationFacadeService.setCurrentLocation(emptyLocationModel());
            }),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  archiveLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.archiveLocation),
      map(action => action.locationId),
      switchMap((locationId: string) =>
        this.locationsService
          .archiveLocation(locationId)
          .pipe(
            tap(location => this.launchToastMssg('success', 'Archive Location', 'The location has been archived')),
            map(location => LocationsActions.archiveLocationSuccess({ locationId })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  editLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.editLocation),
      map(action => action.location),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (location: LocationModel, business: BusinessModel) => ({
          ...location,
          businessId: business.id
        })
      ),
      switchMap((location) =>
        this.locationsService
          .editLocation(location)
          .pipe(
            tap(location => this.launchToastMssg('success', 'Edit Location', `The location ${location.name} has been edited`)),
            map(location => LocationsActions.editLocationSuccess({ location })),
            tap(() => this.routerFacadeService.navigateTo$('admin-location/profile')),
            catchError(error => {
              this.launchToastMssg('error', 'Edit Location', `${error.error.message}`)
              return of(LocationsActions.locationError({ error: error.message }))
            })
          )
      )
    )
  );

  editLocationMaxQuota$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.editLocationMaxQuota),
      map(action => action.location),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (location: LocationModel, business: BusinessModel) => ({
          ...location,
          businessId: business.id
        })
      ),
      switchMap((location) =>
        this.locationsService
          .editLocation(location)
          .pipe(
            tap(location => this.launchToastMssg('success', 'Edit Location Quota', `The location ${location.name} has been edited`)),
            map(location => LocationsActions.editLocationSuccess({ location })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  getUrls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.getUrls),
      map(action => action.locationId),
      switchMap((locationId: string) =>
        this.locationsService
          .getUrls(locationId)
          .pipe(
            map(urls => LocationsActions.getUrlsSuccess({ urls })),
            catchError(error => of(LocationsActions.locationError({ error: error.message }))),
          )
      )
    )
  );

  validateShortName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.validateShortName),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as boolean),
            map(exist => LocationsActions.validateShortNameSuccess({ exist })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  getContactsLast14Days$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.getContactsLast14Days),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as number),
            map(contactCount => LocationsActions.getContactsLast14DaysSuccess({ contactCount })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  getReviewsLast14Days$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.getReviewsLast14Days),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as number),
            map(reviewCount => LocationsActions.getReviewsLast14DaysSuccess({ reviewCount })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  autoselectPreferredLocation$ = createEffect(() =>
    this.store.select(fromApp.getSelectedBusinessProfile).pipe(
      filter(b => !!b.id),
      // only when a new business is selected
      // not if we're loading a business and location simulataneously
      throttleIf(this.store.select(fromApp.isSearchingBusinessAndLocation)),
      // TODO: whenever a new location is selected, we update the preferred location
      combineLatestWith(this.store.select(fromApp.getCurrentLocation)),
      distinctUntilChanged(([prevBusiness, _], [currBusiness, __]) => prevBusiness.id === currBusiness.id),
      //filter(([business, location]) => business.id == location.businessId || !location.id),
      // get locations (and if we're on page 1) to 
      withLatestFrom(
        this.store.select(fromApp.getLocations),
        this.store.select(fromApp.getLocationsPage),
      ),
      switchMap(([[business, location], locations, page]) => {
        console.log('business:', business);
        console.log('location:', location);
        console.log('locations:', locations);

        if (business.id != location.businessId || !location.id) {
          return of(
            // TODO: the locations page needs this, and loads it on page open, but might already be open
            // We should probably switch nearly all searchLocation implementations to this one
            LocationsActions.searchLocationByQueryAndLandingUrls({ page: 0, size: 100, query: '' })
          );
        }

        // TODO: the rest of these likely never run because locations aren't available when
        // the business is selected. Rename this effect to autoloadLocationsAfterBusinessSelection
        // and/or fix preferred location autoselection by creating a designated Action
        //
        // The exception: on a hard refresh -- but this probably only triggers the last else (EMPTY) branch?

        // if there's a preferredLocation but no location selected...
        if (business.preferredLocation && !location.id) {
          const preferredLocation = locations.find(loc => loc.id == business.preferredLocation);

          if (preferredLocation) {
            // if we have the location loaded, just set the location
            return of(LocationsActions.setCurrentLocation({ location: preferredLocation }));
          } else {
            // load the location from the server
            return of(LocationsActions.findLocationById({ locationId: business.preferredLocation }));
          }
        }
        // if there's no preferredLocation, but a location is selected, make it preferred
        else if (!business.preferredLocation && location.id) {
          return of(BusinessActions.editBusinessPreferredLocation({
            preferredLocation: location.id,
          }));
        }
        // if there's no preferredLocation AND a location is not selected, make the first preferred
        else if (!business.preferredLocation && !location.id && locations.length > 0 && page == 0) {
          return of(
            BusinessActions.editBusinessPreferredLocation({ preferredLocation: locations[0].id }),
            LocationsActions.setCurrentLocation({ location: locations[0] }),
          );
        }
        else {
          return EMPTY;
        }
      })
    )
  )
  searchLocationsByQueryAndLandingUrls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.searchLocationByQueryAndLandingUrls),
      map((action) => ({ page: action.page, size: action.size, query: action.query })),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (payload: { page: number, size: number, query: string }, business: BusinessModel) => ({
          ...payload,
          businessId: business.id
        })
      ),
      switchMap(({ page, size, query, businessId }) =>
        this.locationsService.searchLocationsByQueryAndLandingUrls({
          page,
          query,
          size,
          businessId
        }).pipe(
          map(data => LocationsActions.searchLocationsSuccess({ ...data, page })),
          catchError(error => from([
            LocationsActions.searchLocationsFailed(),
            LocationsActions.locationError({ error })
          ]))
        )
      )
    )
  )

  upgradePlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.upgradePlan),
      map(action => action.payload),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        this.store.select(fromApp.getLoggedUser),
        this.store.select(fromApp.getCurrentLocation),
        (payload: { action: UpgradeAction, cancelFeatureButton: string }, business: BusinessModel, user: UserModel, location: LocationModel) => ({
          action: payload.action,
          businessId: business.id,
          businessName: business.name,
          locationId: location.id,
          locationName: location.name,
          userName: `${user.firstName} ${user.lastName}`,
          userEmail: user.email,
          cancelFeatureButton: payload.cancelFeatureButton
        })
      ),
      switchMap((upgradeRequest) =>
        this.locationsService
          .upgradePlan(upgradeRequest)
          .pipe(
            tap(LocationModel => {
              if (upgradeRequest.action != UpgradeAction.CANCEL) {
                this.launchToastMssg('success', 'Upgrade Plan', `Plan has been upgraded`)
              }
            }),
            map(location => LocationsActions.upgradePlanSuccess({ location })),
            catchError(error => of(LocationsActions.locationError({ error: error.message })))
          )
      )
    )
  );

  enableOrDisableAutoReplyAi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.enableOrDisableAutoReplyAi),
      map(action => action.payload),
      switchMap((payload) =>
        this.locationsService
          .enableOrDisableAutoReplyAi(payload)
          .pipe(
            map(location => LocationsActions.enableOrDisableAutoReplyAiSuccess({ location })),
            catchError(error => {
              this.launchToastMssg('Error', 'Enable/Disable Auto Reply AI', `${error.error.message}`)
              return of(LocationsActions.locationError({ error: error.message }))
            })
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private locationsService: LocationsService,
    private routerFacadeService: RouterFacadeService,
    private locationFacadeService: LocationsFacadeService,
    private messageToast: MessageService,
    private store: Store<any>,
    private httpApiService: HttpApiService,
    private window: Window,
  ) { }

  launchToastMssg(severity: string, summary: string, detail: string) {
    this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
  }

}
